import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectIsAudioEnabled, setAudioEnabled } from '../../../app/actions/audio/audioSlice';
import { claimPlayback, releasePlayback } from './playbackArbiter';
import { setSpacePlaybackTarget, clearSpacePlaybackTarget } from './spacePlayback';

/**
 * オーディオ再生エンジン（UI 非依存）。
 *
 * `<audio>` 要素を DOM 外（new Audio()）で管理し、再生・シーク・時刻を提供する。
 * - UI と分離されているため、プレビュー UI を非表示にしても再生は継続できる
 * - 再生中は requestAnimationFrame で currentTime を更新（波形進捗の滑らか描画用）
 * - 再生開始時にグローバル排他アービターへ claim し、アプリ内の同時再生を防ぐ
 * - グローバルミュート（audioSlice）と連動。再生開始時は自動でミュート解除する
 *   （無音再生は意味がないため。既存 AudioPreview と同じ挙動）
 * - 再生開始・シーク時にグローバルなスペースキー再生ターゲット（spacePlayback）へ
 *   自己登録する。「最後にアクティブになった音源」がどこでもスペースキーで
 *   トグルできる（Messenger 含む。Preview もスライドのメディアを登録して参加する）
 */

export interface AudioTransportParams {
  src: string | null | undefined;
  /**
   * 'metadata': マウント時に audio 要素を生成しメタデータ（duration）を取得。
   * 'none': 初回の再生/シークまで audio 要素を生成しない（一覧など大量表示向け）
   */
  preload?: 'none' | 'metadata';
  /** メタデータ取得前に表示する推定 duration（peaks から算出した値等） */
  initialDuration?: number;
  onPlayStateChange?: (_playing: boolean) => void;
  onEnded?: () => void;
}

export interface AudioTransport {
  isPlaying: boolean;
  currentTime: number;
  /** メタデータ取得済みなら実尺、未取得なら initialDuration（無ければ 0） */
  duration: number;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  /** 絶対シーク（秒） */
  seekTo: (_seconds: number) => void;
  /** 相対シーク（秒） */
  seekBy: (_seconds: number) => void;
  /**
   * この音源をスペースキー再生のターゲットにする。
   * 再生・シークでは自動登録されるため、行クリック選択など
   * 再生を伴わないアクティブ化操作から明示的に呼ぶ用
   */
  markActive: () => void;
  /** 内部の audio 要素（未生成時 null）。高度な同期制御用 */
  getAudioElement: () => HTMLAudioElement | null;
}

export function useAudioTransport(params: AudioTransportParams): AudioTransport {
  const { src, preload = 'metadata', initialDuration, onPlayStateChange, onEnded } = params;

  const dispatch = useDispatch();
  const isAudioEnabled = useSelector(selectIsAudioEnabled);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [mediaDuration, setMediaDuration] = useState(0);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  // メタデータ未取得のまま要求されたシーク位置（loadedmetadata 後に適用する）
  const pendingSeekRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const tokenRef = useRef<symbol | null>(null);
  if (!tokenRef.current) tokenRef.current = Symbol('audio-transport');

  const onPlayStateChangeRef = useRef(onPlayStateChange);
  onPlayStateChangeRef.current = onPlayStateChange;
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;
  const isAudioEnabledRef = useRef(isAudioEnabled);
  isAudioEnabledRef.current = isAudioEnabled;

  /** audio 要素を生成し、イベントリスナーを配線する（1 transport につき 1 要素） */
  const ensureAudio = useCallback((): HTMLAudioElement | null => {
    if (!src) return null;
    if (audioRef.current) return audioRef.current;

    const audio = new Audio();
    audio.preload = 'metadata';
    audio.muted = !isAudioEnabledRef.current;
    audio.src = src;

    const syncDuration = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setMediaDuration(audio.duration);
      }
      // 要素生成前に指定された位置は、尺が分かったこの時点で反映する
      const pending = pendingSeekRef.current;
      if (pending != null && audio.readyState > 0) {
        pendingSeekRef.current = null;
        audio.currentTime = Math.max(0, Math.min(audio.duration || pending, pending));
      }
    };
    const handlePlay = () => {
      claimPlayback(tokenRef.current as symbol, () => audio.pause());
      setIsPlaying(true);
      onPlayStateChangeRef.current?.(true);
    };
    const handlePause = () => {
      setIsPlaying(false);
      setCurrentTime(audio.currentTime);
      onPlayStateChangeRef.current?.(false);
    };
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(audio.duration && isFinite(audio.duration) ? audio.duration : 0);
      onPlayStateChangeRef.current?.(false);
      onEndedRef.current?.();
    };
    const handleTimeUpdate = () => {
      // RAF 停止中（一時停止中のシーク等）の時刻同期
      if (audio.paused) setCurrentTime(audio.currentTime);
    };

    audio.addEventListener('loadedmetadata', syncDuration);
    audio.addEventListener('durationchange', syncDuration);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('timeupdate', handleTimeUpdate);

    audioRef.current = audio;
    return audio;
  }, [src]);

  // src 変更・アンマウント時: 要素を破棄して状態をリセット
  useEffect(() => {
    if (preload === 'metadata') {
      ensureAudio();
    }
    return () => {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        // リスナーごと破棄（同一要素は再利用しない）
        audio.src = '';
        audioRef.current = null;
      }
      pendingSeekRef.current = null;
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      releasePlayback(tokenRef.current as symbol);
      setIsPlaying(false);
      setCurrentTime(0);
      setMediaDuration(0);
    };
  }, [src, preload, ensureAudio]);

  // スペースキーのターゲット解除は破棄時のみ行う。
  // src 差し替えではトランスポート自体は生きているうえ、React は子の effect を先に
  // 流すため、上の cleanup で解除すると「選択に追従して markActive した子」の登録を
  // 直後に消してしまう（＝スペースが効かずページがスクロールする）。
  useEffect(() => {
    const token = tokenRef.current as symbol;
    return () => clearSpacePlaybackTarget(token);
  }, []);

  // 再生中は RAF で currentTime を更新（波形進捗の滑らか描画）
  useEffect(() => {
    if (!isPlaying) return;
    let active = true;
    const tick = () => {
      if (!active) return;
      const audio = audioRef.current;
      if (audio) setCurrentTime(audio.currentTime);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      active = false;
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isPlaying]);

  // グローバルミュートの手動 OFF に追従（再生開始時の自動 ON は play 側で行う）
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = !isAudioEnabled;
    }
  }, [isAudioEnabled]);

  // スペースキー再生ターゲットへの自己登録。
  // toggle は毎レンダー参照が変わるため ref 経由の間接呼び出しにして、
  // 登録済みターゲットが常に最新の toggle を叩けるようにする。
  const toggleRef = useRef<() => void>(() => {});
  const markActive = useCallback(() => {
    setSpacePlaybackTarget(tokenRef.current as symbol, () => toggleRef.current());
  }, []);

  const play = useCallback(() => {
    const audio = ensureAudio();
    if (!audio) return;
    markActive();
    // 無音再生は意味がないため、再生開始時にグローバルミュートを自動解除
    dispatch(setAudioEnabled(true));
    audio.muted = false;
    audio.play().catch(error => {
      if ((error as Error)?.name !== 'AbortError') {
        console.warn('[useAudioTransport] 再生に失敗:', error);
      }
    });
  }, [ensureAudio, dispatch, markActive]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (audio && !audio.paused) {
      audio.pause();
    } else {
      play();
    }
  }, [play]);
  toggleRef.current = toggle;

  const seekTo = useCallback((seconds: number) => {
    const audio = ensureAudio();
    if (!audio) return;
    markActive();
    const max = audio.duration && isFinite(audio.duration) ? audio.duration : Infinity;
    const clamped = Math.max(0, Math.min(max, seconds));
    if (audio.readyState > 0) {
      audio.currentTime = clamped;
    } else {
      // メタデータ未取得だと代入しても 0 のままになるため、取得後に適用する
      pendingSeekRef.current = clamped;
    }
    setCurrentTime(clamped);
  }, [ensureAudio, markActive]);

  const seekBy = useCallback((seconds: number) => {
    const audio = ensureAudio();
    if (!audio) return;
    seekTo(audio.currentTime + seconds);
  }, [ensureAudio, seekTo]);

  const getAudioElement = useCallback(() => audioRef.current, []);

  const duration = mediaDuration > 0 ? mediaDuration : (initialDuration ?? 0);

  return useMemo(() => ({
    isPlaying,
    currentTime,
    duration,
    play,
    pause,
    toggle,
    seekTo,
    seekBy,
    markActive,
    getAudioElement,
  }), [isPlaying, currentTime, duration, play, pause, toggle, seekTo, seekBy, markActive, getAudioElement]);
}
